import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { RequireAuth } from './guards/RequireAuth';
import { RequireRole } from './guards/RequireRole';
import { UserRole } from './types/user';

// Public pages - loaded immediately
import { LandingPage } from './pages/landing/LandingPage';
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';

// Lazy load other pages for better initial load performance
const AboutPage = lazy(() => import('./pages/public/AboutPage').then(m => ({ default: m.AboutPage })));
const ContactPage = lazy(() => import('./pages/public/ContactPage').then(m => ({ default: m.ContactPage })));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })));
const OAuthCallback = lazy(() => import('./pages/auth/OAuthCallback').then(m => ({ default: m.OAuthCallback })));
const AdminLayout = lazy(() => import('./components/layout/AdminLayout').then(m => ({ default: m.AdminLayout })));
const ClientLayout = lazy(() => import('./components/layout/ClientLayout').then(m => ({ default: m.ClientLayout })));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const ClientDirectory = lazy(() => import('./pages/admin/ClientDirectory').then(m => ({ default: m.ClientDirectory })));
const ClientDetailPage = lazy(() => import('./pages/admin/ClientDetailPage').then(m => ({ default: m.ClientDetailPage })));
const CreateCasePage = lazy(() => import('./pages/admin/CreateCasePage').then(m => ({ default: m.CreateCasePage })));
const CaseDetail = lazy(() => import('./pages/admin/CaseDetail').then(m => ({ default: m.CaseDetail })));
const ClientDashboard = lazy(() => import('./pages/client/ClientDashboard').then(m => ({ default: m.ClientDashboard })));
const DocumentCenter = lazy(() => import('./pages/client/DocumentCenter').then(m => ({ default: m.DocumentCenter })));
const TaxFilings = lazy(() => import('./pages/client/TaxFilings').then(m => ({ default: m.TaxFilings })));
const SignDocument = lazy(() => import('./pages/client/SignDocument').then(m => ({ default: m.SignDocument })));

const LoadingFallback = () => (
  <div className="min-h-screen w-full bg-surface flex items-center justify-center">
    <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/oauth/callback" element={<OAuthCallback />} />
        
        <Route
          path="/admin"
          element={
            <RequireAuth>
              <RequireRole role={UserRole.ADMIN}>
                <AdminLayout />
              </RequireRole>
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="clients" element={<ClientDirectory />} />
          <Route path="clients/:id" element={<ClientDetailPage />} />
          <Route path="cases/new" element={<CreateCasePage />} />
          <Route path="cases/:id" element={<CaseDetail />} />
        </Route>
        
        <Route
          path="/portal"
          element={
            <RequireAuth>
              <RequireRole role={UserRole.CLIENT}>
                <ClientLayout />
              </RequireRole>
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/portal/dashboard" replace />} />
          <Route path="dashboard" element={<ClientDashboard />} />
          <Route path="documents" element={<DocumentCenter />} />
          <Route path="filings" element={<TaxFilings />} />
          <Route path="sign/:id" element={<SignDocument />} />
        </Route>
        
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
